<template>
    <aside class="mc-sidebar-liveview" data-testid="technician-sidebar">
        <div class="mc-sidebar-liveview__wrapper">
            <b-tabs nav-class="mc-sidebar-liveview__nav" content-class="mc-sidebar-liveview__content">
                <b-tab active>
                    <template #title>
                        <i class="mc-fa fas fa-user-hard-hat me-1" aria-hidden="true"></i>
                        Teknisi
                    </template>

                    <div class="mc-sidebar-liveview__fleet">
                        <div class="mc-sidebar-liveview__fleet-inner">
                            <header class="mc-sidebar-liveview__sticky-header">
                                <div class="mc-filter-group">
                                    <button type="button" class="mc-filter-chip"
                                        :class="{ 'is-active is-all': activeButtons.all }"
                                        @click="changeActiveButton('all')">
                                        <span class="mc-filter-chip__label">Semua</span>
                                        <span class="mc-filter-chip__count">{{ counts.all }}</span>
                                    </button>
                                    <button type="button" class="mc-filter-chip"
                                        :class="{ 'is-active is-west': activeButtons.west }"
                                        @click="changeActiveButton('west')">
                                        <span class="area-badge west">West</span>
                                        <span class="mc-filter-chip__count">{{ counts.west }}</span>
                                    </button>
                                    <button type="button" class="mc-filter-chip"
                                        :class="{ 'is-active is-east': activeButtons.east }"
                                        @click="changeActiveButton('east')">
                                        <span class="area-badge east">East</span>
                                        <span class="mc-filter-chip__count">{{ counts.east }}</span>
                                    </button>
                                </div>

                                <b-input-group class="mc-sidebar-liveview__search mt-3">
                                    <b-input-group-text><i class="icon-outline_search" /></b-input-group-text>
                                    <b-form-input v-model="search.name" type="search" placeholder="Cari teknisi..." />
                                </b-input-group>

                                <p class="mc-sidebar-liveview__summary">
                                    Menampilkan <strong>{{ filteredItems.length }}</strong> dari <strong>{{
                                        technicians.length }}</strong>
                                    teknisi
                                    <span v-if="!activeButtons.all" class="text-muted">({{ activeAreaLabel }})</span>
                                </p>
                            </header>

                            <div class="mc-sidebar-liveview__list-wrapper">
                                <div v-if="isLoading" class="mc-state mc-state--loading">
                                    <b-spinner small variant="primary" />
                                    <span class="ms-2">Memuat data teknisi...</span>
                                </div>
                                <div v-else-if="!filteredItems.length" class="mc-state mc-state--empty">
                                    <i class="mc-fa icon-outline_search text-muted" />
                                    <h6 class="mt-2 mb-1">Tidak ada teknisi</h6>
                                    <small>Coba ubah filter atau kata kunci pencarian.</small>
                                </div>
                                <ul v-else class="mc-tech-list">
                                    <li v-for="tech in filteredItems" :key="tech.user_id" class="mc-tech-item"
                                        :class="`is-area-${(tech.address?.area || 'West').toLowerCase()}`"
                                        @click="selectTechnician(tech)">
                                        <div class="mc-tech-item__avatar"
                                            :class="`area-${(tech.address?.area || 'West').toLowerCase()}`">
                                            {{ getInitials(tech.name) }}
                                        </div>
                                        <div class="mc-tech-item__body">
                                            <div class="mc-tech-item__header">
                                                <h6 class="mc-tech-item__name">{{ tech.name }}</h6>
                                                <span class="mc-tech-item__badge"
                                                    :class="`badge-${(tech.address?.area || 'West').toLowerCase()}`">
                                                    {{ tech.address?.area || 'West' }}
                                                </span>
                                            </div>
                                            <div class="mc-tech-item__meta">
                                                <div class="mc-tech-item__meta-row">
                                                    <i class="mc-fa icon-outline_pin" />
                                                    <span class="text-truncate-2">
                                                        {{ tech.address?.address || 'Alamat tidak tersedia' }}
                                                    </span>
                                                </div>
                                                <div class="mc-tech-item__meta-row">
                                                    <i class="fas fa-map-marker-alt" />
                                                    <span>{{ formatLatLng(tech.address) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </b-tab>
            </b-tabs>
        </div>
    </aside>
</template>

<script>
import responsive from '@/mixin/responsive'
import { Helper } from '@/mixin/helper'
import { mapActions } from 'vuex'

export default {
    name: 'SideBar',
    mixins: [responsive, Helper],
    data() {
        return {
            isLoading: false,
            search: { name: '' },
            activeButtons: {
                all: true,
                west: false,
                east: false,
            },
        }
    },
    computed: {
        technicians() {
            return this.$store.state.users.technicians || []
        },
        counts() {
            const c = { all: this.technicians.length, west: 0, east: 0 };
            this.technicians.forEach(t => {
                const area = t.address?.area;
                if (area === 'West') c.west++;
                else if (area === 'East') c.east++;
            });
            return c;
        },
        filteredItems() {
            let list = this.technicians;
            if (!this.activeButtons.all) {
                list = list.filter(t => {
                    const area = t.address?.area;
                    if (this.activeButtons.west && area === 'West') return true;
                    if (this.activeButtons.east && area === 'East') return true;
                    return false;
                });
            }
            const q = (this.search.name || '').trim().toLowerCase();
            if (q) {
                list = list.filter(t =>
                    (t.name || '').toLowerCase().includes(q) ||
                    (t.address?.address && t.address.address.toLowerCase().includes(q))
                );
            }
            return list;
        },
        activeAreaLabel() {
            return [
                this.activeButtons.west && 'West',
                this.activeButtons.east && 'East',
            ].filter(Boolean).join(', ')
        },
    },
    watch: {
        technicians: {
            handler(val) {
                console.log('🔄 SideBar: technicians berubah, panjang:', val.length);
            },
            deep: true
        }
    },
    mounted() {
        // Sinkronkan state awal dengan store (default all)
        this.$store.commit('map/SET_AREA_FILTER', 'all');
        this.loadData();
    },
    methods: {
        ...mapActions('users', ['loadTechnicians']),
        async loadData() {
            this.isLoading = true;
            try {
                await this.loadTechnicians();

            } finally {
                this.isLoading = false;
            }
        },
        selectTechnician(tech) {
            if (!tech) return;
            this.$store.commit('map/SET_SELECTED_TECHNICIAN', tech);
        },
        changeActiveButton(button) {
            if (button === 'all') {
                this.activeButtons = { all: true, west: false, east: false };
                this.$store.commit('map/SET_AREA_FILTER', 'all');
                return;
            }
            this.activeButtons[button] = !this.activeButtons[button];

            const allAreasActive = ['west', 'east'].every(area => this.activeButtons[area]);
            const noneActive = ['west', 'east'].every(area => !this.activeButtons[area]);

            if (allAreasActive || noneActive) {
                this.activeButtons = { all: true, west: false, east: false };
                this.$store.commit('map/SET_AREA_FILTER', 'all');
            } else {
                this.activeButtons.all = false;
                // Karena hanya satu area yang aktif (toggle), ambil yang aktif
                const filter = this.activeButtons.west ? 'west' : 'east';
                this.$store.commit('map/SET_AREA_FILTER', filter);
            }
        },
        getInitials(name) {
            return (name || '?').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase()
        },
        formatLatLng(addressObj) {
            if (!addressObj) return '-';
            const lat = addressObj.latitude ?? addressObj.lat;
            const lng = addressObj.longitude ?? addressObj.lng;
            if (lat != null && lng != null && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
                return `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
            }
            return '-';
        }
    },
}
</script>

<style scoped>
/* ===================================
   Sidebar Container
=================================== */
.mc-sidebar-liveview {
    position: relative;
    height: 100vh;
    /* Ubah dari 100% menjadi 100vh agar mengunci tinggi layar */
    width: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    overflow: hidden;
}

.mc-sidebar-liveview__wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Tabs */
::v-deep .mc-sidebar-liveview__nav {
    flex-shrink: 0;
    padding: 0 16px;
    background: #ffffff;
    border-bottom: 1px solid #e5e9f0;
}

::v-deep .mc-sidebar-liveview__nav .nav-link {
    font-weight: 600;
    font-size: 0.875rem;
    color: #64748b;
    border: none;
    padding: 12px 16px;
}

::v-deep .mc-sidebar-liveview__nav .nav-link.active {
    color: #2563eb;
    background: transparent;
    border-bottom: 2px solid #2563eb;
}

::v-deep .mc-sidebar-liveview__content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

::v-deep .mc-sidebar-liveview__content>.tab-pane {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

::v-deep .tab-pane {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.mc-sidebar-liveview__fleet {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.mc-sidebar-liveview__fleet-inner {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Sticky Header */
.mc-sidebar-liveview__sticky-header {
    flex-shrink: 0;
    background: #ffffff;
    padding: 16px 16px 12px;
    border-bottom: 1px solid #eef2f7;
    z-index: 2;
}

/* Filter chips */
.mc-filter-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.mc-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #f1f5f9;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s ease;
}

.mc-filter-chip:hover {
    background: #e2e8f0;
}

.mc-filter-chip__count {
    background: #cbd5e1;
    color: #1e293b;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.4;
}

.mc-filter-chip.is-active {
    color: #ffffff;
}

.mc-filter-chip.is-active .mc-filter-chip__count {
    background: rgba(255, 255, 255, 0.25);
    color: #ffffff;
}

.mc-filter-chip.is-active.is-all {
    background: #2563eb;
}

.mc-filter-chip.is-active.is-west {
    background: #3b82f6;
}

.mc-filter-chip.is-active.is-east {
    background: #16a34a;
}

/* Search */
.mc-sidebar-liveview__search ::v-deep .input-group-text {
    background: #f8fafc;
    border-color: #e5e9f0;
    color: #64748b;
}

.mc-sidebar-liveview__search ::v-deep .form-control {
    border-color: #e5e9f0;
    font-size: 0.875rem;
}

.mc-sidebar-liveview__search ::v-deep .form-control:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.mc-sidebar-liveview__summary {
    margin: 12px 0 0;
    font-size: 0.8125rem;
    color: #475569;
}

/* ===================================
   List Wrapper - SCROLL AREA
=================================== */
.mc-sidebar-liveview__list-wrapper {
    flex: 1;
    min-height: 0;
    /* Wajib agar overflow-y bekerja */
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    background: #f8fafc;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

/* Pastikan komponen utama b-tabs menggunakan layout flex vertikal */
::v-deep .tabs {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

::v-deep .tab-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Memaksa pane yang aktif agar berperan sebagai flex container */
::v-deep .tab-content>.tab-pane.active {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

/* Tech List */
.mc-tech-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.mc-tech-item {
    display: flex;
    gap: 12px;
    padding: 14px;
    background: #ffffff;
    border: 1px solid #eef2f7;
    border-left: 4px solid transparent;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.18s ease;
}

.mc-tech-item:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
}

.mc-tech-item:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
}

.mc-tech-item.is-area-west {
    border-left-color: #3b82f6;
}

.mc-tech-item.is-area-east {
    border-left-color: #16a34a;
}

/* Avatar */
.mc-tech-item__avatar {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
    color: #ffffff;
}

.mc-tech-item__avatar.area-west {
    background: #3b82f6;
}

.mc-tech-item__avatar.area-east {
    background: #16a34a;
}

/* Body */
.mc-tech-item__body {
    flex: 1;
    min-width: 0;
}

.mc-tech-item__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.mc-tech-item__name {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-tech-item__badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    white-space: nowrap;
}

.badge-west {
    background: #bfdbfe;
    color: #1e3a8a;
}

.badge-east {
    background: #dcfce7;
    color: #15803d;
}

.mc-tech-item__meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 0.8125rem;
    color: #64748b;
}

.mc-tech-item__meta-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.45;
}

.mc-tech-item__meta-row i {
    width: 14px;
    margin-top: 2px;
}

.text-truncate-2 {
    word-break: break-word;
    overflow-wrap: anywhere;
}

/* State loading/empty */
.mc-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    text-align: center;
    color: #64748b;
}

.mc-state--loading {
    flex-direction: row;
    padding: 24px 16px;
}

.mc-state--empty i {
    font-size: 2rem;
    opacity: 0.4;
}
</style>
